import axios from "axios";
import { templateProviderDiagnostics } from "./utils/template-provider-diagnostics";
import { isMobile, isTablet } from "react-device-detect";
import { getRestApiUrl } from "./utils/backendOverride";
import { attachBearerToken, createJcnErrorRejecter } from "./utils/apiInterceptors";

const API = axios.create({
  baseURL: `${process.env.REACT_APP_REST_API}`,
});

// ---------------------------------------------------------------------------
// Message-send instrumentation helpers
//
// The four routes that the user actually hits when clicking "send" in the
// chat get a small set of metadata headers attached. The matching backend
// middleware (messageSendAuditMiddleware in the iventas-backend repo) reads
// them and persists a row to the MessageSendAuditLog Mongo collection so
// the audit log captures network/device/timing data the backend cannot
// infer on its own.
//
// Internal/service routes (sendMessageiVentas, sendMessageAsesoriaCreativa,
// sendPaymentReminder, etc.) are intentionally NOT instrumented.
// ---------------------------------------------------------------------------

const SEND_ROUTES_REGEX = /\/(sendBotMessageToUser|sendMessageWithFileToUser|sendWhatsappTemplateMessage|forwardMessages)/;

function generateTraceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

function getNetworkInfo() {
  if (typeof navigator === "undefined") return { type: "", downlink: "" };
  const conn =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  if (!conn) return { type: "", downlink: "" };
  return {
    type: conn.effectiveType || "",
    downlink: conn.downlink != null ? String(conn.downlink) : "",
  };
}

function getDeviceType() {
  if (isTablet) return "tablet";
  if (isMobile) return "mobile";
  return "desktop";
}

// ---------------------------------------------------------------------------
// Interceptors
// ---------------------------------------------------------------------------

API.interceptors.request.use((config) => {
  attachBearerToken(config);
  // config.headers["ngrok-skip-browser-warning"] = 'true';
  // Resolved per request: the A/B company is routed to the sin-Prisma backend.
  config.baseURL = `${getRestApiUrl()}`;

  const url = config.url || "";
  if (SEND_ROUTES_REGEX.test(url)) {
    try {
      const traceId = generateTraceId();
      const startedAtClient = Date.now();
      const net = getNetworkInfo();
      // Headers consumed by the backend `messageSendAuditMiddleware`
      config.headers["X-Trace-Id"] = traceId;
      config.headers["X-Client-Started"] = String(startedAtClient);
      config.headers["X-Network-Type"] = net.type;
      config.headers["X-Network-Downlink"] = net.downlink;
      config.headers["X-Device"] = getDeviceType();
      // Stash for the response interceptor (grey→green confirm callback)
      // eslint-disable-next-line no-param-reassign
      config.metadata = { traceId, startedAtClient };
    } catch (e) {
      // Telemetry must NEVER break the request.
      // eslint-disable-next-line no-console
      console.error("[axios-instrumentation] request error (ignored):", e);
    }
  }

  return config;
});

API.interceptors.response.use(
  (response) => {
    // Close the audit cycle: record the moment the message turns green
    // (the FE just received the 200 → updateMessageId sets sent:true).
    const meta = response.config && response.config.metadata;
    if (meta && meta.traceId && meta.startedAtClient) {
      const now = Date.now();
      API.post("/audit/message-confirm", {
        traceId: meta.traceId,
        roundTripMs: now - meta.startedAtClient,
        messageConfirmedAtClient: now,
      }).catch(function () { /* telemetry — never fail */ });
    }
    return response;
  },
  createJcnErrorRejecter(templateProviderDiagnostics)
);

export default API;
